import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import FroalaEditor from 'react-froala-wysiwyg';
import { useTranslation } from 'react-i18next';
import { FloatingLabel } from './FloatingLabel';

import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';

import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/code_beautifier.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/js/plugins/emoticons.min.js';
import 'froala-editor/js/plugins/entities.min.js';
import 'froala-editor/js/plugins/font_family.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/help.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/inline_class.min.js';
import 'froala-editor/js/plugins/inline_style.min.js';
import 'froala-editor/js/plugins/line_breaker.min.js';
import 'froala-editor/js/plugins/line_height.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/paragraph_style.min.js';
import 'froala-editor/js/plugins/print.min.js';
import 'froala-editor/js/plugins/quick_insert.min.js';
import 'froala-editor/js/plugins/quote.min.js';
import 'froala-editor/js/plugins/special_characters.min.js';
import 'froala-editor/js/plugins/table.min.js';
import 'froala-editor/js/plugins/url.min.js';
import 'froala-editor/js/plugins/word_counter.min.js';
import 'froala-editor/js/plugins/word_paste.min.js';

import 'froala-editor/css/plugins/char_counter.min.css';
import 'froala-editor/css/plugins/code_view.min.css';
import 'froala-editor/css/plugins/colors.min.css';
import 'froala-editor/css/plugins/draggable.min.css';
import 'froala-editor/css/plugins/emoticons.min.css';
import 'froala-editor/css/plugins/help.min.css';
import 'froala-editor/css/plugins/image.min.css';
import 'froala-editor/css/plugins/line_breaker.min.css';
import 'froala-editor/css/plugins/quick_insert.min.css';
import 'froala-editor/css/plugins/special_characters.min.css';
import 'froala-editor/css/plugins/table.min.css';

import "froala-editor/js/languages/ar";
import "froala-editor/js/languages/bs";
import "froala-editor/js/languages/cs";
import "froala-editor/js/languages/da";
import "froala-editor/js/languages/de";
import "froala-editor/js/languages/el";
import "froala-editor/js/languages/en_ca";
import "froala-editor/js/languages/en_gb";
import "froala-editor/js/languages/es";
import "froala-editor/js/languages/et";
import "froala-editor/js/languages/fa";
import "froala-editor/js/languages/fi";
import "froala-editor/js/languages/fr";
import "froala-editor/js/languages/he";
import "froala-editor/js/languages/hr";
import "froala-editor/js/languages/hu";
import "froala-editor/js/languages/id";
import "froala-editor/js/languages/it";
import "froala-editor/js/languages/ja";
import "froala-editor/js/languages/ko";
import "froala-editor/js/languages/ku";
import "froala-editor/js/languages/me";
import "froala-editor/js/languages/nb";
import "froala-editor/js/languages/nl";
import "froala-editor/js/languages/pl";
import "froala-editor/js/languages/pt_br";
import "froala-editor/js/languages/pt_pt";
import "froala-editor/js/languages/ro";
import "froala-editor/js/languages/ru";
import "froala-editor/js/languages/sk";
import "froala-editor/js/languages/sl";
import "froala-editor/js/languages/sr";
import "froala-editor/js/languages/sv";
import "froala-editor/js/languages/th";
import "froala-editor/js/languages/tr";
import "froala-editor/js/languages/uk";
import "froala-editor/js/languages/vi";
import "froala-editor/js/languages/zh_cn";
import "froala-editor/js/languages/zh_tw";
import useClientConfig from '../hooks/clientConfigHook';
import { HiddenValidationControl } from './HiddenValidationControl';
import PropTypes from 'prop-types';
import { withComponentCapabilities } from './capabilities/componentCapabilities';
import { materializeValue } from '../utils/lazyValue';


export const customCtrlDef = {
    toString: async ({ value, defaultValue }) => {
        value = await materializeValue(value);
        if (!value) {
            return defaultValue;
        }
        return value.replace(/<br>/g, '\n').replace(/<li>/ig, ' - ').replace(/<\/li>/ig, '\n').replace(/<\/p>/g, '\n\n').replace(/<[^>]+>/g, '').replace('&nbsp;', ' ');
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, href, ...props }) => <RichTextEditor href={href ?? def.href} {...props}></RichTextEditor>
};

const definedLangs = ['ar','bs','cs','da','de','el','en_ca','en_gb','es','et','fa','fi','fr','he','hr','hu','id','it','ja','ko','ku','me','nb','nl','pl','pt_br','pt_pt','ro','ru','sk','sl','sr','sv','th','tr','uk','vi','zh_cn','zh_tw'];

const RichTextEditor = withComponentCapabilities(forwardRef(({ value: inputValue, live, placeholder, hideLabel, disabled, required, readonly, onChange, href }, ref) => {
    const { i18n } = useTranslation();
    const clientConfig = useClientConfig();
    const [value, setValue] = useState(inputValue);
    const [lang, setLang] = useState('en')
    const handleBlurRef = useRef();
    const prevInput = useRef(inputValue);
    const validationRef = useRef();

    let editorInstance = useRef(null); // Stores the editor instance in the ref

    const handleChange = useCallback(value => {
        setValue(value);
        if (live && inputValue !== value) {
            onChange?.(value);
        }
    }, [onChange, inputValue, live])

    handleBlurRef.current = (e, editor) => {
        if (value !== prevInput.current && validationRef.current?.checkValidity()) {
            onChange?.(value);
        }
    };

    useEffect(() => {
        prevInput.current = inputValue;
        setValue(inputValue)
    }, [inputValue]);

    useEffect(() => {
        (async () => {
            let newLang = i18n.language.toLocaleLowerCase().replace('-', '_');
            if (!definedLangs.some(l => l === newLang)) {
                newLang = newLang.split('_')[0];
            }
            if (newLang !== lang && definedLangs.some(l => l === newLang)) {
                setLang(newLang);
            }
        })();
    }, [i18n.language, lang])

    const config = useMemo(() => ({
        placeholderText: placeholder,
        key: clientConfig.froalaKey,
        attribution: false,
        language: lang,
        useClasses: false,
        toolbarSticky: false,
        toolbarButtons: {
            moreText: {
                buttons: ['bold', 'italic', 'underline', 'textColor', 'backgroundColor', 'clearFormatting', 'strikeThrough', 'subscript', 'superscript', 'fontFamily', 'fontSize', 'inlineClass', 'inlineStyle',],
                buttonsVisible: 6
            },
            moreParagraph: {
                buttons: ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify', 'formatOLSimple', 'formatUL', 'formatOL', 'paragraphFormat', 'paragraphStyle', 'lineHeight', 'outdent', 'indent', 'quote'],
                buttonsVisible: 6
            },
            moreRich: {
                buttons: ['insertLink', 'insertImage', 'insertTable', 'emoticons', 'specialCharacters', 'insertHR'],
                buttonsVisible: 6
            },
            moreMisc: {
                buttons: ['undo', 'redo', 'html', 'help'],
                align: 'right',
                buttonsVisible: 4
            }
        },
        toolbarButtonsMD: {
            moreText: {
                buttons: ['bold', 'italic', 'underline', 'textColor', 'backgroundColor', 'clearFormatting', 'strikeThrough', 'subscript', 'superscript', 'fontFamily', 'fontSize', 'inlineClass', 'inlineStyle',],
                buttonsVisible: 3
            },
            moreParagraph: {
                buttons: ['alignLeft', 'alignCenter', 'formatOLSimple', 'formatUL', 'alignRight', 'alignJustify', 'formatOL', 'paragraphFormat', 'paragraphStyle', 'lineHeight', 'outdent', 'indent', 'quote'],
                buttonsVisible: 3
            },
            moreRich: {
                buttons: ['insertLink', 'insertImage', 'insertTable', 'emoticons', 'specialCharacters', 'insertHR'],
                buttonsVisible: 3
            },
            moreMisc: {
                buttons: ['undo', 'redo', 'html', 'help'],
                align: 'right',
                buttonsVisible: 4
            }
        },
        toolbarButtonsSM: {
            moreText: {
                buttons: ['bold', 'italic', 'underline', 'textColor', 'backgroundColor', 'clearFormatting', 'strikeThrough', 'subscript', 'superscript', 'fontFamily', 'fontSize', 'inlineClass', 'inlineStyle',],
                buttonsVisible: 2
            },
            moreParagraph: {
                buttons: ['alignLeft', 'alignCenter', 'formatOLSimple', 'formatUL', 'alignRight', 'alignJustify', 'formatOL', 'paragraphFormat', 'paragraphStyle', 'lineHeight', 'outdent', 'indent', 'quote'],
                buttonsVisible: 2
            },
            moreRich: {
                buttons: ['insertLink', 'insertImage', 'insertTable', 'emoticons', 'specialCharacters', 'insertHR'],
                buttonsVisible: 2
            },
            moreMisc: {
                buttons: ['undo', 'redo', 'html', 'help'],
                align: 'right',
                buttonsVisible: 2
            }
        },
        toolbarButtonsXS: {
            moreText: {
                buttons: ['bold', 'italic', 'underline', 'textColor', 'backgroundColor', 'clearFormatting', 'strikeThrough', 'subscript', 'superscript', 'fontFamily', 'fontSize', 'inlineClass', 'inlineStyle',],
                buttonsVisible: 0
            },
            moreParagraph: {
                buttons: ['alignLeft', 'alignCenter', 'formatOLSimple', 'formatUL', 'alignRight', 'alignJustify', 'formatOL', 'paragraphFormat', 'paragraphStyle', 'lineHeight', 'outdent', 'indent', 'quote'],
                buttonsVisible: 0
            },
            moreRich: {
                buttons: ['insertLink', 'insertImage', 'insertTable', 'emoticons', 'specialCharacters', 'insertHR'],
                buttonsVisible: 0
            },
            moreMisc: {
                buttons: ['undo', 'redo', 'html', 'help'],
                align: 'right',
                buttonsVisible: 2
            }
        },
        events: {
            blur: (e, editor) => { handleBlurRef.current?.(e, editor) },
            "image.beforeUpload": function (files) {
                let editor = this;
                if (files.length) {
                    // Create a File Reader.
                    const reader = new FileReader();
                    // Set the reader to insert images when they are loaded.
                    reader.onload = function (e) {
                        const result = e.target.result;
                        editor.image.insert(result, null, null, editor.image.get());
                    };
                    // Read image as base64.
                    reader.readAsDataURL(files[0]);
                }
                editor.popups.hideAll();
                // Stop default upload chain.
                return false;
            }
        }
    }), [placeholder, lang, clientConfig.froalaKey]);

    // Expose the injectText function to the parent via ref
    useImperativeHandle(ref, () => ({
      injectText(text) {
        if (editorInstance.current) {
          editorInstance.current.editor.html.insert(text); // Inserts text at the current cursor position
        }
      },
      setContent(content) {
        if (editorInstance.current) {
          editorInstance.current.editor.html.set(content); // Replaces the whole content with the provided content
        }
      }
    }));


    return <FloatingLabel placeholder={placeholder} hideLabel={hideLabel} value={value} href={href}>
        {readonly || disabled ? <div dangerouslySetInnerHTML={{ __html: value }}></div>
            : <>
                <FroalaEditor key={lang} tag="textarea" config={config} model={value ?? ''} onModelChange={handleChange} ref={editorInstance}></FroalaEditor>
                <HiddenValidationControl ref={validationRef} value={value} required={required}></HiddenValidationControl>
              </>}
    </FloatingLabel>
}), {

});
RichTextEditor.propTypes = {
    /**
     * The html to be displayed by the control
     */
    value: PropTypes.string,
    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,
    /**
     * Function to call when the input value has been changed
     */
    onChange: PropTypes.func,
    /**
     * The label for the control
     */
    placeholder: PropTypes.string,
    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,
    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,
    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,
     /**
      * Ref passed from the parent component that exposes `injectText` and `setContent` methods
      * */
    ref: PropTypes.func,
    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default RichTextEditor;